import React from 'react';
import { cx } from '../../../../../ui/components/cx';

export interface SideNavItem {
  key: string;
  label: React.ReactNode;
  disabled?: boolean;
  title?: string;
}

interface SideNavProps {
  items: SideNavItem[];
  /** Key of the active item; compared case-insensitively. */
  activeKey?: string;
  onSelect: (key: string) => void;
  /** Accessible name for the nav landmark. */
  label: string;
  /** Text on the toggle that opens the list on narrow screens. */
  toggleLabel?: React.ReactNode;
  open?: boolean;
  onToggle?: () => void;
}

/**
 * Vertical section nav for profile-style pages. On narrow screens it collapses
 * behind a toggle and opens as an overlay; from 1200px it is a static column.
 */
const SideNav: React.FC<SideNavProps> = ({ items, activeKey, onSelect, label, toggleLabel, open, onToggle }) => {
  const listId = React.useId();
  const active = (activeKey ?? '').toLowerCase();
  return (
    <nav className={cx('ds-sidenav', open && 'ds-sidenav--open')} aria-label={label}>
      {onToggle && (
        <button
          type="button"
          className="ds-sidenav__toggle"
          aria-expanded={!!open}
          aria-controls={listId}
          onClick={onToggle}
        >
          {toggleLabel}
        </button>
      )}
      <ul id={listId} className="ds-sidenav__list">
        {items.map((item) => {
          const isActive = item.key.toLowerCase() === active;
          return (
            <li key={item.key}>
              <button
                type="button"
                className={cx('ds-sidenav__item', isActive && 'ds-sidenav__item--active')}
                aria-current={isActive ? 'page' : undefined}
                disabled={item.disabled}
                title={item.title}
                onClick={() => onSelect(item.key)}
              >
                {item.label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default SideNav;
